import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { sendNotifications } from '../../services/emailService';
import SeoHead from '../../components/SeoHead';

const recursos = [
  {
    id: 1,
    titulo: 'Adaptar el CV al mercado europeo',
    descripcion: 'Estructura tu currículum según los estándares europeos y destaca en procesos de selección internacional.',
    icon: 'ri-file-text-line',
  },
  {
    id: 2,
    titulo: 'Preparar entrevistas internacionales',
    descripcion: 'Estrategias y técnicas para afrontar entrevistas con empresas europeas con confianza y criterio.',
    icon: 'ri-user-voice-line',
  },
  {
    id: 3,
    titulo: 'Claves culturales en Alemania',
    descripcion: 'Puntualidad, jerarquía y comunicación directa en el entorno laboral alemán.',
    icon: 'ri-flag-line',
    flag: '🇩🇪',
  },
  {
    id: 4,
    titulo: 'Claves culturales en Suiza',
    descripcion: 'Precisión, neutralidad y protocolos de trabajo en un entorno corporativo internacional.',
    icon: 'ri-flag-line',
    flag: '🇨🇭',
  },
  {
    id: 5,
    titulo: 'Claves culturales en Países Bajos',
    descripcion: 'Horizontalidad, consenso y comunicación abierta en el modelo holandés.',
    icon: 'ri-flag-line',
    flag: '🇳🇱',
  },
  {
    id: 6,
    titulo: 'Claves culturales en Austria',
    descripcion: 'Formalidad, calidad y excelencia profesional en el mercado laboral austriaco.',
    icon: 'ri-flag-line',
    flag: '🇦🇹',
  },
  {
    id: 7,
    titulo: 'Claves culturales en Bélgica',
    descripcion: 'Bilingüismo, diversidad regional y networking en el entorno multicultural belga.',
    icon: 'ri-flag-line',
    flag: '🇧🇪',
  },
];

const pilares = [
  {
    icon: 'ri-archive-line',
    titulo: '7 guías especializadas',
    descripcion: 'CV europeo, entrevistas internacionales y claves culturales por país.',
  },
  {
    icon: 'ri-refresh-line',
    titulo: 'Actualizaciones incluidas',
    descripcion: 'El contenido evoluciona con las tendencias del mercado europeo.',
  },
  {
    icon: 'ri-lock-unlock-line',
    titulo: 'Acceso permanente',
    descripcion: 'Disponible desde el momento del pago, sin caducidad ni suscripción.',
  },
];

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const bibliotecaSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Biblioteca Estratégica Internacional – BN ĀGENZIA',
  description: 'Colección de 7 guías profesionales para posicionarse en el mercado laboral europeo: CV europeo, entrevistas internacionales y claves culturales por país.',
  url: `${siteUrl}/biblioteca-estrategica`,
  brand: { '@type': 'Brand', name: 'BN ĀGENZIA' },
  offers: {
    '@type': 'Offer',
    price: '29',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/PreOrder',
    seller: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  },
  inLanguage: 'es',
};

export default function BibliotecaEstrategicaPage() {
  const navigate = useNavigate();
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [notifyFormData, setNotifyFormData] = useState({ nombre: '', email: '' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!privacyAccepted) return;

    setIsSubmitting(true);

    try {
      await sendNotifications(
        'Interés Biblioteca Estratégica',
        {
          nombre: notifyFormData.nombre,
          email: notifyFormData.email,
          fecha: new Date().toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
        notifyFormData.email,
        notifyFormData.nombre
      );

      setShowSuccess(true);
      setNotifyFormData({ nombre: '', email: '' });
      setPrivacyAccepted(false);
      setTimeout(() => {
        setShowSuccess(false);
        setShowNotifyForm(false);
      }, 3000);
    } catch (error) {
      console.error('Error al enviar notificación:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SeoHead
        title="Biblioteca Estratégica Internacional – Guías para el Mercado Europeo | BN ĀGENZIA"
        description="7 guías profesionales para posicionarte en Europa: CV europeo, entrevistas internacionales y claves culturales de Alemania, Suiza, Países Bajos, Austria y Bélgica. Acceso por 29€."
        keywords="biblioteca estratégica, guías empleo Europa, CV europeo, entrevistas internacionales, mercado laboral europeo"
        canonical="/biblioteca-estrategica"
        schema={bibliotecaSchema}
      />
      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-24 right-6 z-50 bg-green-50 border border-green-200 rounded-lg px-6 py-4 shadow-lg animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-green-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-green-900">¡Registrado correctamente!</p>
              <p className="text-xs text-green-700">Te avisaremos cuando esté disponible</p>
            </div>
          </div>
        </div>
      )}

      {/* Notify Modal */}
      {showNotifyForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => { setShowNotifyForm(false); setPrivacyAccepted(false); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#1A2B4A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-line text-[#1A2B4A] text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Te avisaremos</h3>
              <p className="text-sm text-gray-600">
                Déjanos tu email y te notificaremos cuando la biblioteca esté disponible
              </p>
            </div>

            <form onSubmit={handleNotifySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre completo
                </label>
                <input
                  type="text"
                  required
                  value={notifyFormData.nombre}
                  onChange={(e) => setNotifyFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={notifyFormData.email}
                  onChange={(e) => setNotifyFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent"
                  placeholder="tu@email.com"
                />
              </div>

              {/* Checkbox Política de Privacidad */}
              <div className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  id="biblioteca-privacy-checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-[#1A2B4A] border-gray-300 rounded focus:ring-[#1A2B4A] cursor-pointer flex-shrink-0"
                />
                <label htmlFor="biblioteca-privacy-checkbox" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                  He leído y acepto la{' '}
                  <a
                    href="/politica-privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1A2B4A] font-medium hover:underline"
                  >
                    Política de Privacidad
                  </a>
                  .
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !privacyAccepted}
                className="w-full px-6 py-3 bg-[#1A2B4A] text-white text-sm font-semibold rounded-lg hover:bg-[#243550] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line"></i>
                    Notificarme
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A] rounded-lg">
                <i className="ri-book-shelf-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Biblioteca Estratégica Internacional</h1>
                <p className="text-xs text-gray-600">Recursos profesionales para el mercado europeo</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              Volver al inicio
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F1E35] via-[#1A2B4A] to-[#243550] py-24">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full mb-8">
            <i className="ri-star-line text-white/80 text-sm"></i>
            <span className="text-white/90 text-xs font-semibold uppercase tracking-widest">Acceso completo</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Biblioteca Estratégica<br />Internacional
          </h1>
          
          <p className="text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mx-auto mb-10">
            Una colección de recursos profesionales diseñados para quienes buscan posicionarse con rigor en el mercado laboral europeo. Criterios reales de selección, contexto cultural y estrategia profesional en un único acceso.
          </p>

          {/* Precio destacado */}
          <div className="inline-block bg-white rounded-2xl px-12 py-8 shadow-2xl mb-6">
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-2">Acceso completo</p>
            <div className="flex items-end justify-center gap-1 mb-2">
              <span className="text-6xl font-extrabold text-[#1A2B4A] leading-none">29</span>
              <span className="text-3xl font-bold text-[#1A2B4A] mb-2">€</span>
            </div>
            <p className="text-gray-600 text-sm mb-4">Pago único · Sin suscripción</p>
            <div className="h-px bg-gray-200 mb-4"></div>
            <p className="text-sm text-gray-700 leading-relaxed max-w-md">
              Diseñado para profesionales que buscan posicionarse con ventaja real en procesos internacionales.
            </p>
          </div>

          {/* CTA Principal */}
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={() => setShowNotifyForm(true)}
              className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#1A2B4A] text-base font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-notification-line text-xl"></i>
              Avísame cuando esté disponible
            </button>
            <p className="text-white/50 text-xs tracking-wide">Lanzamiento oficial próximamente.</p>
            <p className="text-white/40 text-xs flex items-center gap-2 mt-1">
              <i className="ri-shield-check-line"></i>
              Pago seguro · Acceso inmediato y permanente
            </p>
          </div>
        </div>
      </section>

      {/* Pilares de valor */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#1A2B4A]/50 mb-4">
              Por qué esta biblioteca
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A2B4A] mb-4">
              Acceso estratégico y permanente
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              No es un curso genérico. Es una colección de recursos especializados que condensan años de experiencia en selección internacional.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pilares.map((pilar) => (
              <div key={pilar.titulo} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 flex items-center justify-center bg-[#1A2B4A]/10 rounded-xl mb-5">
                  <i className={`${pilar.icon} text-[#1A2B4A] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{pilar.titulo}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{pilar.descripcion}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contenido de la biblioteca */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#1A2B4A]/50 mb-4">
              Contenido incluido
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A2B4A] mb-4">
              7 guías especializadas
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Todo lo que necesitas para prepararte con criterio y diferenciarte en procesos de selección internacional.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {recursos.map((recurso) => (
              <div
                key={recurso.id}
                className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col gap-4"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#1A2B4A]/8 rounded-lg flex-shrink-0">
                  {recurso.flag ? (
                    <span className="text-2xl">{recurso.flag}</span>
                  ) : (
                    <i className={`${recurso.icon} text-[#1A2B4A] text-2xl`}></i>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{recurso.titulo}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{recurso.descripcion}</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-gray-400 pt-3 border-t border-gray-100">
                  <i className="ri-lock-line"></i>
                  <span>Disponible tras el acceso</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qué obtienes */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#1A2B4A]/50 mb-4">
              Qué obtienes
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A2B4A] mb-4">
              Más que información: estrategia aplicable
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/10 rounded-lg flex-shrink-0">
                <i className="ri-file-list-3-line text-[#1A2B4A] text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Estructura profesional del CV europeo</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Aprende a adaptar tu currículum a los estándares de selección internacional y optimizarlo para sistemas ATS.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/10 rounded-lg flex-shrink-0">
                <i className="ri-user-voice-line text-[#1A2B4A] text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Preparación para entrevistas internacionales</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Técnicas y estrategias para afrontar entrevistas con empresas europeas con confianza y criterio.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/10 rounded-lg flex-shrink-0">
                <i className="ri-global-line text-[#1A2B4A] text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Claves culturales por país</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Comprende las expectativas profesionales, protocolos de comunicación y dinámicas laborales en 5 países clave.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/10 rounded-lg flex-shrink-0">
                <i className="ri-time-line text-[#1A2B4A] text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Actualizaciones continuas</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  El contenido se actualiza regularmente según las tendencias del mercado laboral europeo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-[#0F1E35] via-[#1A2B4A] to-[#243550]">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full mb-8">
            <i className="ri-vip-crown-line text-white/80 text-sm"></i>
            <span className="text-white/90 text-xs font-semibold uppercase tracking-widest">Acceso premium</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
            Posiciónate con ventaja real en el mercado europeo
          </h2>
          
          <p className="text-lg text-white/85 leading-relaxed mb-10 max-w-2xl mx-auto">
            Un único pago de <strong className="text-white">29€</strong> te da acceso inmediato y permanente a toda la biblioteca, con actualizaciones futuras incluidas. Sin suscripciones ni costes adicionales.
          </p>

          <div className="flex flex-col items-center gap-3">
            <button
              onClick={() => setShowNotifyForm(true)}
              className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#1A2B4A] text-base font-bold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-notification-line text-xl"></i>
              Avísame cuando esté disponible
            </button>
            <p className="text-white/50 text-xs tracking-wide">Lanzamiento oficial próximamente.</p>
            <p className="text-white/40 text-xs flex items-center gap-2 mt-1">
              <i className="ri-shield-check-line"></i>
              Pago seguro · Acceso inmediato · Sin suscripción
            </p>
          </div>
        </div>
      </section>

      {/* Footer minimalista */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-[#1A2B4A] rounded-lg">
                <i className="ri-building-line text-white text-sm"></i>
              </div>
              <span className="text-sm font-semibold text-gray-900">BN ĀGENZIA</span>
            </div>
            <p className="text-xs text-gray-500">
              © 2025 BN ĀGENZIA · Selección internacional especializada
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}